
/* eslint-disable no-var */
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ntxAPI } from '../../../API/axiosNeatex';
import type { APIResponse } from '@store/Shared/shared';

// ==================== النماذج (Models) ====================

export interface PosScaleProfile {
  id: string;
  code: string;
  name: string;
  name2: string;
  isActive: boolean;
  notes: string;
  createdDate?: string;
  modifiedDate?: string;
}

export interface PosScaleRule {
  id: string;
  profileId: string;
  ruleType: number;
  isEnabled: boolean;
  prefix: string;
  barcodeLength: number;
  itemCodeLength: number;
  rightTrim: number;
  weightBase: number;
  priceDecimals: number;
  useEmbeddedPriceAsLineTotal: boolean;
  deriveQtyFromEmbeddedPrice: boolean;
  createdDate?: string;
  modifiedDate?: string;
}

export interface PosScaleProfileCreate {
  code: string;
  name: string;
  name2: string;
  isActive: boolean;
  notes: string;
}

export interface PosScaleRuleCreate {
  profileId: string;
  ruleType: number;
  isEnabled: boolean;
  prefix: string;
  barcodeLength: number;
  itemCodeLength: number;
  rightTrim: number;
  weightBase: number;
  priceDecimals: number;
  useEmbeddedPriceAsLineTotal: boolean;
  deriveQtyFromEmbeddedPrice: boolean;
}

export interface PrefixConflictCheck {
  hasConflict: boolean;
  conflictingRule?: PosScaleRule;
}

// ==================== Async Thunks لملفات التعريف ====================

export const addPosScaleProfile = createAsyncThunk(
  'posScale/addPosScaleProfile',
  async (profile: PosScaleProfileCreate, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post<APIResponse<PosScaleProfile>>('Pos/AddPosScaleProfile', profile);
      if (data?.succeeded) {
        return data.data;
      } else {
        return rejectWithValue(data?.message || 'فشل في إنشاء ملف التعريف');
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updatePosScaleProfile = createAsyncThunk(
  'posScale/updatePosScaleProfile',
  async (profile: PosScaleProfile, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post<APIResponse<PosScaleProfile>>('Pos/UpdatePosScaleProfile', profile);
      if (data?.succeeded) {
        return data.data;
      } else {
        return rejectWithValue(data?.message || 'فشل في تحديث ملف التعريف');
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deletePosScaleProfile = createAsyncThunk(
  'posScale/deletePosScaleProfile',
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post<APIResponse<boolean>>(`Pos/DeletePosScaleProfile?id=${id}`);
      if (data?.succeeded) {
        return { id, success: data.data };
      } else {
        return rejectWithValue(data?.message || 'فشل في حذف ملف التعريف');
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);
 
export const checkPosScaleProfileByCode = createAsyncThunk(
  'posScale/checkPosScaleProfileByCode',
  async (code: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get<APIResponse<boolean>>(`Pos/CheckPosScaleProfileByCode?code=${code}`);
      if (data?.succeeded) {
        return { code, exists: data.data };
      } else {
        return rejectWithValue(data?.message || 'فشل في التحقق من الكود');
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getPosScaleProfileById = createAsyncThunk(
  'posScale/getPosScaleProfileById',
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get<APIResponse<PosScaleProfile>>(`Pos/GetPosScaleProfileById?id=${id}`);
      if (data?.succeeded) {
        return data.data;
      } else {
        return rejectWithValue(data?.message || 'فشل في جلب ملف التعريف');
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getPosScaleProfileList = createAsyncThunk(
  'posScale/getPosScaleProfileList',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get<APIResponse<PosScaleProfile[]>>('Pos/GetPosScaleProfileList');
      if (data?.succeeded) {
        return data.data;
      } else {
        return rejectWithValue(data?.message || 'فشل في جلب قائمة ملفات التعريف');
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==================== Async Thunks للقواعد ====================

export const addPosScaleRule = createAsyncThunk(
  'posScale/addPosScaleRule',
  async (rule: PosScaleRuleCreate, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post<APIResponse<PosScaleRule>>('Pos/AddPosScaleRule', rule);
      if (data?.succeeded) {
        return data.data;
      } else {
        return rejectWithValue(data?.message || 'فشل في إنشاء القاعدة');
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updatePosScaleRule = createAsyncThunk(
  'posScale/updatePosScaleRule',
  async (rule: PosScaleRule, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post<APIResponse<PosScaleRule>>('Pos/UpdatePosScaleRule', rule);
      if (data?.succeeded) {
        return data.data;
      } else {
        return rejectWithValue(data?.message || 'فشل في تحديث القاعدة');
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deletePosScaleRule = createAsyncThunk(
  'posScale/deletePosScaleRule',
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post<APIResponse<boolean>>(`Pos/DeletePosScaleRule?id=${id}`);
      if (data?.succeeded) {
        return { id, success: data.data };
      } else {
        return rejectWithValue(data?.message || 'فشل في حذف القاعدة');
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const checkPosScaleRulePrefixConflict = createAsyncThunk(
  'posScale/checkPosScaleRulePrefixConflict',
  async (prefix: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get<APIResponse<PrefixConflictCheck>>(
        `Pos/CheckPosScaleRulePrefixConflict?prefix=${prefix}`
      );
      if (data?.succeeded) {
        return data.data;
      } else {
        return rejectWithValue(data?.message || 'فشل في التحقق من البادئة');
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getPosScaleRuleById = createAsyncThunk(
  'posScale/getPosScaleRuleById',
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get<APIResponse<PosScaleRule>>(`Pos/GetPosScaleRuleById?id=${id}`);
      if (data?.succeeded) {
        return data.data;
      } else {
        return rejectWithValue(data?.message || 'فشل في جلب القاعدة');
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getPosScaleRuleByProfileId = createAsyncThunk(
  'posScale/getPosScaleRuleByProfileId',
  async (profileId: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get<APIResponse<PosScaleRule[]>>(
        `Pos/GetPosScaleRulesByProfileId?ProfileId=${profileId}`
      );
      if (data?.succeeded) {
        return data.data;
      } else {
        return rejectWithValue(data?.message || 'فشل في جلب قواعد ملف التعريف');
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==================== الحالة الأولية (Initial State) ====================

interface PosScaleState {
  // ملفات التعريف
  profiles: PosScaleProfile[];
  currentProfile: PosScaleProfile | null;
  
  // القواعد
  rules: PosScaleRule[];
  profileRules: PosScaleRule[]; // قواعد ملف التعريف المحدد
  currentRule: PosScaleRule | null;
  
  // حالات التحميل
  loading: boolean;
  loadingProfiles: boolean;
  loadingRules: boolean;
  loadingProfile: boolean;
  loadingRule: boolean;
  
  // أخطاء
  error: string | null;
  errorProfiles: string | null;
  errorRules: string | null;
  errorProfile: string | null;
  errorRule: string | null;
  
  // نتائج التحقق
  codeCheck: { code: string; exists: boolean } | null;
  prefixCheck: PrefixConflictCheck | null;
}

const initialState: PosScaleState = {
  // البيانات
  profiles: [],
  currentProfile: null,
  rules: [],
  profileRules: [],
  currentRule: null,
  
  // حالات التحميل
  loading: false,
  loadingProfiles: false,
  loadingRules: false,
  loadingProfile: false,
  loadingRule: false,
  
  // الأخطاء
  error: null,
  errorProfiles: null,
  errorRules: null,
  errorProfile: null,
  errorRule: null,
  
  // نتائج التحقق
  codeCheck: null,
  prefixCheck: null,
};

// ==================== الـ Slice ====================

const posScaleSlice = createSlice({
  name: 'posScale',
  initialState,
  reducers: {
    clearCurrentProfile: (state) => {
      state.currentProfile = null;
      state.errorProfile = null;
    },
    clearCurrentRule: (state) => {
      state.currentRule = null;
      state.errorRule = null;
    },
    clearCodeCheck: (state) => {
      state.codeCheck = null;
    },
    clearPrefixCheck: (state) => {
      state.prefixCheck = null;
    },
    clearErrors: (state) => {
      state.error = null;
      state.errorProfiles = null;
      state.errorRules = null;
      state.errorProfile = null;
      state.errorRule = null;
    },
  },
  extraReducers: (builder) => {
    // ========== معالجات ملفات التعريف ==========
    
    // جلب قائمة ملفات التعريف
    builder
      .addCase(getPosScaleProfileList.pending, (state) => {
        state.loadingProfiles = true;
        state.errorProfiles = null;
      })
      .addCase(getPosScaleProfileList.fulfilled, (state, action: PayloadAction<PosScaleProfile[]>) => {
        state.loadingProfiles = false;
        state.profiles = action.payload;
      })
      .addCase(getPosScaleProfileList.rejected, (state, action: PayloadAction<any>) => {
        state.loadingProfiles = false;
        state.errorProfiles = action.payload;
      });

    // جلب ملف تعريف محدد
    builder
      .addCase(getPosScaleProfileById.pending, (state) => {
        state.loadingProfile = true;
        state.errorProfile = null;
      })
      .addCase(getPosScaleProfileById.fulfilled, (state, action: PayloadAction<PosScaleProfile>) => {
        state.loadingProfile = false;
        state.currentProfile = action.payload;
      })
      .addCase(getPosScaleProfileById.rejected, (state, action: PayloadAction<any>) => {
        state.loadingProfile = false;
        state.errorProfile = action.payload;
      });

    // إنشاء ملف تعريف
    builder
      .addCase(addPosScaleProfile.fulfilled, (state, action: PayloadAction<PosScaleProfile>) => {
        state.profiles.push(action.payload);
      })
      .addCase(addPosScaleProfile.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload;
      });

    // تحديث ملف تعريف
    builder
      .addCase(updatePosScaleProfile.fulfilled, (state, action: PayloadAction<PosScaleProfile>) => {
        const index = state.profiles.findIndex(profile => profile.id === action.payload.id);
        if (index !== -1) {
          state.profiles[index] = action.payload;
        }
        if (state.currentProfile?.id === action.payload.id) {
          state.currentProfile = action.payload;
        }
      })
      .addCase(updatePosScaleProfile.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload;
      });

    // حذف ملف تعريف
    builder
      .addCase(deletePosScaleProfile.fulfilled, (state, action: PayloadAction<{ id: string; success: boolean }>) => {
        if (action.payload.success) {
          state.profiles = state.profiles.filter(profile => profile.id !== action.payload.id);
          if (state.currentProfile?.id === action.payload.id) {
            state.currentProfile = null;
          }
        }
      })
      .addCase(deletePosScaleProfile.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload;
      });

    // التحقق من الكود
    builder
      .addCase(checkPosScaleProfileByCode.fulfilled, (state, action: PayloadAction<{ code: string; exists: boolean }>) => {
        state.codeCheck = action.payload;
      })
      .addCase(checkPosScaleProfileByCode.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload;
      });

    // ========== معالجات القواعد ==========
    
    // جلب قواعد ملف تعريف محدد
    builder
      .addCase(getPosScaleRuleByProfileId.pending, (state) => {
        state.loadingRules = true;
        state.errorRules = null;
      })
      .addCase(getPosScaleRuleByProfileId.fulfilled, (state, action: PayloadAction<PosScaleRule[]>) => {
        state.loadingRules = false;
        state.profileRules = action.payload;
      })
      .addCase(getPosScaleRuleByProfileId.rejected, (state, action: PayloadAction<any>) => {
        state.loadingRules = false;
        state.errorRules = action.payload;
      });

    // جلب قاعدة محددة
    builder
      .addCase(getPosScaleRuleById.pending, (state) => {
        state.loadingRule = true;
        state.errorRule = null;
      })
      .addCase(getPosScaleRuleById.fulfilled, (state, action: PayloadAction<PosScaleRule>) => {
        state.loadingRule = false;
        state.currentRule = action.payload;
      })
      .addCase(getPosScaleRuleById.rejected, (state, action: PayloadAction<any>) => {
        state.loadingRule = false;
        state.errorRule = action.payload;
      });

    // إنشاء قاعدة
    builder
      .addCase(addPosScaleRule.fulfilled, (state, action: PayloadAction<PosScaleRule>) => {
        state.profileRules.push(action.payload);
      })
      .addCase(addPosScaleRule.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload;
      });

    // تحديث قاعدة
    builder
      .addCase(updatePosScaleRule.fulfilled, (state, action: PayloadAction<PosScaleRule>) => {
        const index = state.profileRules.findIndex(rule => rule.id === action.payload.id);
        if (index !== -1) {
          state.profileRules[index] = action.payload;
        }
        if (state.currentRule?.id === action.payload.id) {
          state.currentRule = action.payload;
        }
      })
      .addCase(updatePosScaleRule.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload;
      });

    // حذف قاعدة
    builder
      .addCase(deletePosScaleRule.fulfilled, (state, action: PayloadAction<{ id: string; success: boolean }>) => {
        if (action.payload.success) {
          state.profileRules = state.profileRules.filter(rule => rule.id !== action.payload.id);
          if (state.currentRule?.id === action.payload.id) {
            state.currentRule = null;
          }
        }
      })
      .addCase(deletePosScaleRule.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload;
      });

    // التحقق من البادئة
    builder
      .addCase(checkPosScaleRulePrefixConflict.fulfilled, (state, action: PayloadAction<PrefixConflictCheck>) => {
        state.prefixCheck = action.payload;
      })
      .addCase(checkPosScaleRulePrefixConflict.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload;
      });
  },
});

export const {
  clearCurrentProfile,
  clearCurrentRule,
  clearCodeCheck,
  clearPrefixCheck,
  clearErrors,
} = posScaleSlice.actions;

export default posScaleSlice.reducer;